<script setup lang="ts">
import type { CartItem } from 'types/models/cart.model'
import type { OrderItem } from 'types/models/order'
import { NuxtLink } from '#components'
import { productUrl } from 'lib/routing'
import { QUANTITY_REQUEST_UPPER_LIMIT } from '~/configuration/global.configuration'

interface Props {
  item: OrderItem | CartItem
  simple?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  simple: false,
})

const cartStore = useCartStore()
const gtm = useGTM()

const item = toRef(props, 'item')
const state = reactive({
  loading: false,
  imageError: false,
  removingItem: false,
  maxQtyReached: false,
  errorMessage: '',
})

const isDiscounted = computed(() => item.value.price.final < item.value.price.original)
const disableQtyInteraction = computed(() => {
  const array: Array<'increase' | 'decrease'> = []
  if (item.value.quantity === 1)
    array.push('decrease')
  if (state.maxQtyReached)
    array.push('increase')
  return array
})
const path = productUrl(item.value.parentSku)
const image = computed(() =>
  !item.value.image || state.imageError ? '/icons/filters/crew-sock.svg' : item.value.image,
)

const isNotPurchasable = computed(() => {
  return item.value.isGift
})

function setFallbackImage() {
  state.imageError = true
}
async function removeItem() {
  try {
    await cartStore.removeItem(item.value.itemId)
    gtm.pushRemoveFromCart(item.value, item.value.quantity)
    return Promise.resolve()
  }
  catch (e) {
    return Promise.reject(e)
  }
}
async function updateQty(quantity: number): Promise<void> {
  try {
    return await cartStore.updateItemQty({
      id: item.value.itemId,
      quantity,
    })
  }
  catch (e) {
    return Promise.reject(e)
  }
}
async function onDecrease(input?: number) {
  state.errorMessage = ''
  state.loading = true
  try {
    await updateQty(input || -1)
    gtm.pushRemoveFromCart(item.value, 1)
  }
  catch (e) {
    console.error(e)
  }
  finally {
    state.loading = false
  }
}
async function onIncrease(input?: number) {
  state.errorMessage = ''
  state.loading = true
  try {
    await updateQty(input || 1)
    gtm.pushAddToCart({ item: item.value, source: 'cartPage' })
  }
  catch (error: any) {
    state.errorMessage = error.data.message
    console.error(error)
  }
  finally {
    state.loading = false
  }
}

async function onChangeInput(inputValue: number) {
  state.loading = true
  const currentQuantity = item.value.quantity
  const inputElement = document.querySelector('[data-test=\'quantity-input\']') as HTMLInputElement
  const regExp = /^[1-9]\d*$/

  try {
    if (!regExp.test(inputValue.toString()) || inputValue <= 0) {
      throw new Error('NaN')
    }

    if (inputValue > 10000) {
      state.errorMessage = useNuxtApp().$t(QUANTITY_REQUEST_UPPER_LIMIT)

      throw new Error(QUANTITY_REQUEST_UPPER_LIMIT)
    }

    const difference = inputValue - currentQuantity

    if (difference > 0) {
      onIncrease(difference)
    }
    else {
      onDecrease(difference)
    }
  }
  catch (e) {
    inputElement.setAttribute('value', currentQuantity.toString())
    inputElement.value = currentQuantity.toString()
    state.loading = false
  }
}

async function onRemove() {
  state.removingItem = true
  await removeItem()
  state.removingItem = false
}
</script>

<template>
  <article
    class="item"
    :class="[{ simple }]"
  >
    <component
      :is="isNotPurchasable ? 'div' : NuxtLink"
      :to="{ path }"
    >
      <img
        v-if="state.imageError"
        class="img placeholder"
        :src="image"
        :alt="item.name || $t('productThumbnail')"
      >
      <ImageResponsive
        v-else
        provider="fastly"
        class="img"
        :src="image"
        :alt="item.name || $t('productThumbnail')"
        :max-width="320"
        @error="setFallbackImage"
      />
    </component>
    <div class="item-details">
      <div class="top-row">
        <div class="container">
          <span
            class="name"
            data-test="cart-item-name"
          >
            {{ item.name }}
            <span
              v-if="simple"
              data-test="cart-item-simple-quantity"
            >({{ item.quantity }})</span>
          </span>
          <span> {{ item.size.value }} </span>
          <div>
            <span
              class="unit-price"
              :class="[{ 'line-through': isDiscounted }]"
            > {{ $price(item.price.original, undefined, false) }}</span>
            <span
              v-if="isDiscounted"
              class="unit-price"
            > {{ $price(item.price.final, undefined, false) }}</span>
          </div>
        </div>
        <div class="container aligned">
          <span
            class="price"
            data-test="cart-item-price-total"
          >
            {{ $price((item.price.original - item.productDiscount) * item.quantity, undefined, false) }}
          </span>
          <span
            v-if="'externalTaxAmount' in item && item.externalTaxAmount"
            class="tax"
            data-test="cart-item-external-tax"
          >
            {{ $t('VAT') }} {{ $price(item.externalTaxAmount, undefined, false) }}
          </span>
        </div>
      </div>
      <div
        v-if="!isNotPurchasable"
        class="bottom-row"
      >
        <CartQuantityPill
          :quantity="item.quantity"
          :loading="state.loading"
          :disable-interaction="disableQtyInteraction"
          :error-message="state.errorMessage"
          @increase="onIncrease"
          @decrease="onDecrease"
          @input-change="onChangeInput"
        />
        <button
          class="remove"
          :aria-label="$t('removeItemFromCart')"
          :title="$t('removeItemFromCart')"
          :disabled="state.loading || state.removingItem"
          @click.stop="onRemove"
        >
          <LoadingSpinner
            v-if="state.removingItem"
            :size="1.5"
          />
          <IconBasic
            v-else
            filename="trash"
          />
        </button>
      </div>
    </div>
  </article>
  <div class="divider" />
</template>

<style lang="scss" scoped>
.item {
  display: flex;
  position: relative;
  line-height: 1.8rem;
  &.simple {
    .img {
      width: 3.2rem;
    }
    .bottom-row {
      display: none;
    }
    .unit-price {
      display: none;
    }
  }
  .img {
    width: 8rem;
    margin-right: 2rem;
    .details {
      width: calc(100% - 9rem);
      padding: 0.5rem 0 0 2rem;
    }
  }
  .img .top-row,
  .bottom-row {
    position: relative;
  }
  .bottom-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .remove {
    width: 2rem;
    height: 2rem;
    margin: 2rem 0rem 2rem 3rem;
    span.icon {
      width: 2rem;
      height: 2rem;
    }
  }
  & a {
    outline-offset: -2px;
  }
}

.item-details {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;

  .top-row {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 1rem;
    align-items: end;

    .container {
      display: flex;
      flex-direction: column;

      &.aligned {
        align-self: start;
      }

      span {
        font-size: 1.4rem;
        line-height: 1.6rem;
        margin-bottom: 0.4rem;
      }
      .unit-price {
        font-size: 1.2rem;
        line-height: 1.4rem ;
        padding-right: 0.5rem;
        color: gray
      }

    }
  }

  .tax {
    grid-column-end: 3;
    margin-bottom: 0;
    font-size: 1.2rem;
  }
  .price {
    text-align: end;
  }
  .line-through {
    text-decoration: line-through;
  }
}

.divider {
  border-top: 1px solid var(--pink);
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
}
</style>
